import { Link, createFileRoute } from '@tanstack/react-router';
import { useState, type ComponentType } from 'react';
import {
  ArrowRight,
  BarChart3,
  Boxes,
  CircleCheck,
  FileText,
  Landmark,
  Receipt,
  Repeat,
  Sparkles,
  Store,
} from 'lucide-react';
import { PRODUCT_NAME } from '@/components/brand/logo';
import { TryTheDemo } from '@/components/demo-chooser';
import { ProductPreview } from '@/components/marketing/product-preview';
import { SiteFooter, SiteHeader } from '@/components/marketing/site-chrome';
import { WorldSection } from '@/components/marketing/world-section';
import { Button } from '@/components/ui/button';
import { auth } from '@/lib/auth';
import { useT } from '@/lib/i18n';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/')({ component: HomePage });

type Icon = ComponentType<{ className?: string }>;

/** The product families, listed editorially beside the product preview (text: publicPages.productList). */
type ProductKey =
  'sales' | 'expenses' | 'banking' | 'gst' | 'reports' | 'inventory' | 'ai' | 'migration';
const PRODUCTS: Array<{ icon: Icon; key: ProductKey }> = [
  { icon: FileText, key: 'sales' },
  { icon: Receipt, key: 'expenses' },
  { icon: Landmark, key: 'banking' },
  { icon: Store, key: 'gst' },
  { icon: BarChart3, key: 'reports' },
  { icon: Boxes, key: 'inventory' },
  { icon: Sparkles, key: 'ai' },
  { icon: Repeat, key: 'migration' },
];

/** The kinds of business in the photo strip (photos: public/photos, credits in CREDITS.md). */
const TRADES = [
  { src: '/photos/cafe-owner.webp', key: 'cafes' },
  { src: '/photos/restaurant-owner.webp', key: 'restaurants' },
  { src: '/photos/store-owner.webp', key: 'retail' },
  { src: '/photos/studio-owner.webp', key: 'studios' },
] as const;

/** "How it works" tabs; each has a small sample screen (text: publicPages.flows.<key>…). */
const FLOWS = ['sell', 'spend', 'bank', 'close'] as const;
type Flow = (typeof FLOWS)[number];

function HomePage() {
  return (
    <div className="min-h-screen bg-brand-paper text-brand-ink">
      <SiteHeader />
      <main>
        <Hero />
        <Trades />
        <Products />
        <HowItWorks />
        <Audiences />
        <WorldSection />
        <IndiaReady />
        <CallToAction />
      </main>
      <SiteFooter />
    </div>
  );
}

function Eyebrow({ children, onDark }: { children: string; onDark?: boolean }) {
  return (
    <div
      className={cn(
        'flex items-center gap-3 text-xs font-semibold tracking-[0.16em] uppercase rtl:tracking-normal',
        onDark ? 'text-brand-violet-200' : 'text-primary',
      )}
    >
      <span className={cn('h-px w-8', onDark ? 'bg-brand-violet-200' : 'bg-primary')} />
      {children}
    </div>
  );
}

/** Staggers an entrance animation (motion-safe only, see index.css). */
const after = (ms: number) => ({ animationDelay: `${ms}ms` });

function Hero() {
  const t = useT();
  return (
    <section className="bg-ledger relative overflow-hidden border-b">
      <div className="relative mx-auto max-w-7xl px-6 pt-14 pb-10 text-center">
        <span
          style={after(0)}
          className="motion-safe:animate-rise inline-flex items-center gap-2 rounded-full border border-brand-violet-100 bg-white px-3 py-1 text-xs font-semibold text-primary"
        >
          <Sparkles className="size-3.5" /> {t('publicPages.hero.badge')}
        </span>
        <h1
          style={after(90)}
          className="motion-safe:animate-rise mx-auto mt-6 max-w-5xl text-4xl font-bold leading-[1.08] tracking-[-0.03em] sm:text-5xl lg:text-[3.5rem]"
        >
          {t('publicPages.hero.titleLead')}
          <br className="hidden sm:block" />{' '}
          <span className="bg-gradient-to-r from-primary to-brand-sky bg-clip-text text-transparent">
            {t('publicPages.hero.titleAccent')}
          </span>
        </h1>
        <p
          style={after(180)}
          className="motion-safe:animate-rise mx-auto mt-5 max-w-2xl text-lg leading-relaxed text-brand-slate"
        >
          {t('publicPages.hero.lead', { product: PRODUCT_NAME })}
        </p>
        <div
          style={after(270)}
          className="motion-safe:animate-rise mt-8 flex flex-wrap items-center justify-center gap-3"
        >
          <Button asChild size="lg" className="group h-12 px-6 text-base font-semibold">
            <Link to="/signup">
              {t('publicPages.hero.getStarted')}{' '}
              <ArrowRight className="rtl:-scale-x-100 size-4 transition-transform group-hover:translate-x-0.5 rtl:group-hover:-translate-x-0.5" />
            </Link>
          </Button>
          {/* the public demo when this site has one (ADR-0020); Log in stays in the header */}
          {auth.demo ? (
            <TryTheDemo className="h-12 px-6 text-base font-semibold text-brand-ink" />
          ) : (
            <Button
              asChild
              size="lg"
              variant="outline"
              className="h-12 px-6 text-base font-semibold text-brand-ink"
            >
              <Link to="/login">{t('publicPages.header.logIn')}</Link>
            </Button>
          )}
        </div>
        <div style={after(360)} className="motion-safe:animate-rise mx-auto mt-12 max-w-5xl">
          <ProductPreview />
        </div>
      </div>
    </section>
  );
}

function Trades() {
  const t = useT();
  return (
    <section className="bg-white py-20">
      <div className="mx-auto max-w-7xl px-6">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div className="max-w-2xl">
            <Eyebrow>{t('publicPages.footer.whoFor')}</Eyebrow>
            <h2 className="mt-4 font-display text-4xl font-medium tracking-[-0.01em]">
              {t('publicPages.trades.title')}
            </h2>
          </div>
          <p className="max-w-md text-brand-slate">{t('publicPages.trades.body')}</p>
        </div>
        <div className="mt-10 grid grid-cols-2 items-start gap-3 sm:gap-4 lg:grid-cols-4">
          {TRADES.map((trade, i) => (
            <figure
              key={trade.key}
              className={cn(
                'group relative overflow-hidden rounded-2xl',
                i % 2 === 1 ? 'lg:mt-10' : '',
              )}
            >
              <img
                src={trade.src}
                alt={t(`publicPages.trades.${trade.key}Alt`)}
                loading="lazy"
                className="aspect-[3/4] w-full object-cover transition duration-500 group-hover:scale-[1.03]"
              />
              <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-4 pt-16 text-sm font-semibold text-white">
                {t(`publicPages.trades.${trade.key}`)}
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}

function Products() {
  const t = useT();
  return (
    <section id="products" className="bg-dots scroll-mt-16 border-y py-24">
      <div className="mx-auto max-w-7xl px-6">
        <div className="max-w-3xl">
          <Eyebrow>{t('publicPages.header.products')}</Eyebrow>
          <h2 className="mt-4 font-display text-4xl font-medium tracking-[-0.01em]">
            {t('publicPages.productList.title')}
          </h2>
          <p className="mt-4 max-w-lg text-lg leading-relaxed text-brand-slate">
            {t('publicPages.productList.body')}
          </p>
        </div>
        <ul className="mt-12 grid gap-x-10 sm:grid-cols-2 lg:grid-cols-4">
          {PRODUCTS.map((p, i) => (
            <li key={p.key} className="border-t border-brand-ink/15 py-6">
              <div className="flex items-center gap-3">
                <span className="font-display text-sm text-primary tnum">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <p.icon className="size-4 text-primary" />
                <h3 className="font-semibold">{t(`publicPages.productList.${p.key}Title`)}</h3>
              </div>
              <p className="mt-2 text-sm leading-relaxed text-brand-slate">
                {t(`publicPages.productList.${p.key}Body`)}
              </p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function HowItWorks() {
  const t = useT();
  const [active, setActive] = useState<Flow>(FLOWS[0]);
  const flow = active;
  return (
    <section id="how-it-works" className="scroll-mt-16 bg-white py-24">
      <div className="mx-auto max-w-7xl px-6">
        <Eyebrow>{t('publicPages.header.howItWorks')}</Eyebrow>
        <h2 className="mt-4 max-w-3xl font-display text-4xl font-medium tracking-[-0.01em]">
          {t('publicPages.flows.title')}
        </h2>
        <div
          role="tablist"
          aria-label={t('publicPages.header.howItWorks')}
          className="mt-10 flex flex-wrap gap-8 border-b"
        >
          {FLOWS.map((f, i) => (
            <button
              key={f}
              role="tab"
              type="button"
              aria-selected={f === active}
              onClick={() => setActive(f)}
              className={cn(
                '-mb-px flex items-baseline gap-2 border-b-2 pb-3 text-base font-semibold transition',
                f === active
                  ? 'border-primary text-brand-ink'
                  : 'border-transparent text-brand-slate hover:text-brand-ink',
              )}
            >
              <span className="font-display text-sm text-primary">0{i + 1}</span>
              {t(`publicPages.flows.${f}Label`)}
            </button>
          ))}
        </div>
        <div
          key={flow}
          role="tabpanel"
          className="motion-safe:animate-fade mt-10 grid items-center gap-12 md:grid-cols-2"
        >
          <div>
            <h3 className="font-display text-3xl font-medium">
              {t(`publicPages.flows.${flow}Title`)}
            </h3>
            <p className="mt-4 leading-relaxed text-brand-slate">
              {t(`publicPages.flows.${flow}Body`)}
            </p>
            <ul className="mt-6 space-y-3">
              {(['P1', 'P2', 'P3'] as const).map((pt) => (
                <li key={pt} className="flex items-start gap-3 text-sm">
                  <CircleCheck className="mt-0.5 size-4 shrink-0 text-success" />
                  {t(`publicPages.flows.${flow}${pt}`)}
                </li>
              ))}
            </ul>
          </div>
          <div aria-hidden="true" className="bg-ledger rounded-2xl border p-8 ps-24">
            {flow === 'sell' ? <InvoiceVisual /> : null}
            {flow === 'spend' ? <PurchaseVisual /> : null}
            {flow === 'bank' ? <BankVisual /> : null}
            {flow === 'close' ? <CloseVisual /> : null}
          </div>
        </div>
      </div>
    </section>
  );
}

const Chip = ({ tone, children }: { tone: string; children: string }) => (
  <span className={cn('rounded px-1.5 py-0.5 text-[10px] font-bold uppercase', tone)}>
    {children}
  </span>
);
const GREEN = 'bg-[#e3f4ea] text-success';
const BLUE = 'bg-brand-violet-50 text-brand-violet-700';

function Row({ l, r, strong }: { l: string; r: string; strong?: boolean }) {
  return (
    <div className={cn('flex justify-between py-1 text-xs', strong && 'text-sm font-bold')}>
      <span className={strong ? '' : 'text-brand-slate'}>{l}</span>
      <span className="tnum">{r}</span>
    </div>
  );
}

function InvoiceVisual() {
  const t = useT();
  return (
    <div className="rounded-lg bg-white p-5 shadow-sm">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[11px] font-semibold tracking-wider text-brand-slate uppercase rtl:tracking-normal">
            {t('publicPages.visuals.invoiceNo', { no: 'INV-1042' })}
          </div>
          <div className="mt-1 font-semibold">Kaveri Foods Pvt Ltd</div>
        </div>
        <Chip tone={GREEN}>{t('publicPages.visuals.paid')}</Chip>
      </div>
      <div className="mt-4 border-t pt-3">
        <Row l={t('publicPages.visuals.catering')} r="₹1,20,000.00" />
        <Row l={t('publicPages.visuals.delivery')} r="₹5,000.00" />
        <Row l="CGST 9%" r="₹11,250.00" />
        <Row l="SGST 9%" r="₹11,250.00" />
      </div>
      <div className="mt-2 border-t pt-2">
        <Row l={t('publicPages.visuals.total')} r="₹1,47,500.00" strong />
      </div>
    </div>
  );
}

function PurchaseVisual() {
  const t = useT();
  return (
    <div className="flex items-center gap-3">
      <div className="flex-1 rounded-lg bg-white p-4 shadow-sm">
        <div className="text-[11px] font-semibold tracking-wider text-brand-slate uppercase rtl:tracking-normal">
          {t('publicPages.visuals.purchaseOrder')}
        </div>
        <div className="mt-1 text-sm font-semibold">PO-0031</div>
        <div className="mt-1 text-xs text-brand-slate">Sharma Packaging</div>
        <div className="mt-3 text-sm font-bold tnum">₹64,900.00</div>
        <div className="mt-2">
          <Chip tone={GREEN}>{t('publicPages.visuals.approved')}</Chip>
        </div>
      </div>
      <ArrowRight className="rtl:-scale-x-100 size-5 shrink-0 text-primary" />
      <div className="flex-1 rounded-lg bg-white p-4 shadow-sm ring-2 ring-primary">
        <div className="text-[11px] font-semibold tracking-wider text-brand-slate uppercase rtl:tracking-normal">
          {t('publicPages.visuals.bill')}
        </div>
        <div className="mt-1 text-sm font-semibold">
          {t('publicPages.visuals.fromPo', { no: 'PO-0031' })}
        </div>
        <div className="mt-1 text-xs text-brand-slate">{t('publicPages.visuals.dueIn14')}</div>
        <div className="mt-3 text-sm font-bold tnum">₹64,900.00</div>
        <div className="mt-2">
          <Chip tone={BLUE}>{t('publicPages.visuals.scheduled')}</Chip>
        </div>
      </div>
    </div>
  );
}

function BankVisual() {
  const t = useT();
  // bank statement descriptions stay as the bank prints them
  const lines = [
    {
      d: 'NEFT KAVERI FOODS',
      a: '+₹1,47,500.00',
      m: t('publicPages.visuals.invoiceNo', { no: 'INV-1042' }),
      ok: true,
    },
    {
      d: 'UPI AIRTEL BROADBAND',
      a: '−₹1,179.00',
      m: t('publicPages.visuals.ruleInternet'),
      ok: true,
    },
    {
      d: 'IMPS SHARMA PACKAGING',
      a: '−₹64,900.00',
      m: t('publicPages.visuals.billFromPo', { no: 'PO-0031' }),
      ok: true,
    },
    { d: 'CASH DEPOSIT', a: '+₹20,000.00', m: t('publicPages.visuals.needsCategory'), ok: false },
  ];
  return (
    <div className="overflow-hidden rounded-lg bg-white shadow-sm">
      <div className="flex items-center justify-between border-b px-4 py-2.5 text-xs font-semibold">
        <span>{t('publicPages.visuals.bankHeader', { account: 'HDFC Current' })}</span>
        <Chip tone={BLUE}>{t('publicPages.visuals.matched', { n: 3, total: 4 })}</Chip>
      </div>
      {lines.map((l) => (
        <div key={l.d} className="flex items-center gap-3 border-b px-4 py-2.5 last:border-0">
          <CircleCheck className={cn('size-4 shrink-0', l.ok ? 'text-success' : 'text-border')} />
          <div className="min-w-0 flex-1">
            <div className="truncate text-xs font-medium">{l.d}</div>
            <div className={cn('text-[11px]', l.ok ? 'text-brand-slate' : 'text-warning')}>
              {l.m}
            </div>
          </div>
          <span className="text-xs font-semibold tnum">{l.a}</span>
        </div>
      ))}
    </div>
  );
}

function CloseVisual() {
  const t = useT();
  const items = [
    { key: 'reconciled', locked: false },
    { key: 'gstReviewed', locked: false },
    { key: 'signOff', locked: false },
    { key: 'monthLocked', locked: true },
  ] as const;
  return (
    <div className="rounded-lg bg-white p-5 shadow-sm">
      <div className="flex items-center justify-between">
        <div className="font-semibold">{t('publicPages.visuals.monthEndClose')}</div>
        <span className="text-xs font-semibold text-success">
          {t('publicPages.visuals.progress', { n: 4, total: 4 })}
        </span>
      </div>
      <div className="mt-3 h-2 rounded-full bg-[#e3f4ea]">
        <div className="h-2 w-full rounded-full bg-brand-green" />
      </div>
      <ul className="mt-4 space-y-2.5">
        {items.map((i) => (
          <li key={i.key} className="flex items-center justify-between text-sm">
            <span className="flex items-center gap-2">
              <CircleCheck className="size-4 text-success" />
              {t(`publicPages.visuals.${i.key}`)}
            </span>
            <Chip tone={i.locked ? BLUE : GREEN}>
              {i.locked ? t('publicPages.visuals.locked') : t('publicPages.visuals.done')}
            </Chip>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Audiences() {
  const t = useT();
  const cards = [
    { img: '/photos/cafe-owner-greens.webp', key: 'owners' },
    { img: '/photos/accountant-laptop.webp', key: 'accountants' },
  ] as const;
  return (
    <section id="audiences" className="bg-dots scroll-mt-16 border-y py-24">
      <div className="mx-auto max-w-7xl px-6">
        <Eyebrow>{t('publicPages.audiences.eyebrow')}</Eyebrow>
        <h2 className="mt-4 max-w-3xl font-display text-4xl font-medium tracking-[-0.01em]">
          {t('publicPages.audiences.title')}
        </h2>
        <div className="mt-12 grid gap-8 md:grid-cols-2">
          {cards.map((c) => (
            <article key={c.key} className="overflow-hidden rounded-2xl border bg-white">
              <img
                src={c.img}
                alt={t(`publicPages.audiences.${c.key}Alt`)}
                loading="lazy"
                className="aspect-[16/8] w-full object-cover"
              />
              <div className="p-8">
                <h3 className="font-display text-2xl font-medium">
                  {t(`publicPages.audiences.${c.key}Title`)}
                </h3>
                <p className="mt-3 leading-relaxed text-brand-slate">
                  {t(`publicPages.audiences.${c.key}Body`)}
                </p>
                <ul className="mt-6 space-y-3">
                  {(['P1', 'P2', 'P3'] as const).map((pt) => (
                    <li key={pt} className="flex items-start gap-3 text-sm">
                      <CircleCheck className="mt-0.5 size-4 shrink-0 text-primary" />
                      {t(`publicPages.audiences.${c.key}${pt}`)}
                    </li>
                  ))}
                </ul>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function IndiaReady() {
  const t = useT();
  const facts = [
    { k: 'GST', v: t('publicPages.india.gst') },
    { k: '₹', v: t('publicPages.india.rupee') },
    { k: 'HSN/SAC', v: t('publicPages.india.hsn') },
    { k: t('publicPages.india.eInvoiceKey'), v: t('publicPages.india.eInvoice') },
  ];
  return (
    <section id="india" className="bg-jaali scroll-mt-16 bg-brand-violet-900 text-white">
      <div className="grid items-stretch lg:grid-cols-[1fr_1.15fr]">
        <div className="relative min-h-[320px] overflow-hidden">
          <img
            src="/photos/cafe-interior.webp"
            alt={t('publicPages.india.photoAlt')}
            loading="lazy"
            className="absolute inset-0 h-full w-full object-cover"
          />
        </div>
        <div className="max-w-2xl px-6 py-20 lg:px-16">
          <Eyebrow onDark>{t('publicPages.header.indiaReady')}</Eyebrow>
          <h2 className="mt-4 font-display text-4xl font-medium tracking-[-0.01em]">
            {t('publicPages.india.title')}
          </h2>
          <p className="mt-4 text-lg leading-relaxed text-white/75">
            {t('publicPages.india.body')}
          </p>
          <dl className="mt-10 grid gap-x-10 gap-y-8 sm:grid-cols-2">
            {facts.map((f) => (
              <div key={f.k} className="border-t border-white/20 pt-4">
                <dt className="font-display text-2xl text-brand-violet-200">{f.k}</dt>
                <dd className="mt-1 text-sm leading-relaxed text-white/80">{f.v}</dd>
              </div>
            ))}
          </dl>
        </div>
      </div>
    </section>
  );
}

function CallToAction() {
  const t = useT();
  return (
    <section className="relative overflow-hidden">
      <img
        src="/photos/coffee-shop-evening.webp"
        alt=""
        loading="lazy"
        className="absolute inset-0 h-full w-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-brand-ink-2/95 rtl:bg-gradient-to-l via-brand-ink-2/85 to-brand-ink-2/40" />
      <div className="relative mx-auto max-w-7xl px-6 py-28 text-white">
        <h2 className="max-w-2xl font-display text-5xl leading-tight font-medium tracking-[-0.01em]">
          {t('publicPages.cta.title', { product: PRODUCT_NAME })}
        </h2>
        <p className="mt-5 max-w-xl text-lg text-white/80">{t('publicPages.cta.body')}</p>
        <div className="mt-9 flex flex-wrap gap-3">
          <Button
            asChild
            size="lg"
            className="h-12 rounded-full bg-white px-7 text-base font-semibold text-primary hover:bg-brand-violet-50"
          >
            <Link to="/signup">
              {t('publicPages.header.signUp')} <ArrowRight className="rtl:-scale-x-100 size-4" />
            </Link>
          </Button>
          {auth.demo ? (
            <TryTheDemo className="h-12 rounded-full border-white/50 bg-transparent px-7 text-base font-semibold text-white hover:bg-white/10 hover:text-white" />
          ) : (
            <Button
              asChild
              size="lg"
              variant="outline"
              className="h-12 rounded-full border-white/50 bg-transparent px-7 text-base font-semibold text-white hover:bg-white/10 hover:text-white"
            >
              <Link to="/login">{t('publicPages.header.logIn')}</Link>
            </Button>
          )}
        </div>
      </div>
    </section>
  );
}
